import { useEffect, useState } from "react";
import { api, formatErr, inr, API } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { BookOpen, ArrowDownCircle, ArrowUpCircle, FileDown, Printer } from "lucide-react";
import { toast } from "sonner";
import { PaginationBar, usePagination } from "@/components/Pagination";

function today() { return new Date().toISOString().slice(0, 10); }
function daysAgo(n) { const d = new Date(); d.setDate(d.getDate() - n); return d.toISOString().slice(0, 10); }

export default function DayBook() {
  const [from, setFrom] = useState(daysAgo(7));
  const [to, setTo] = useState(today());
  const [data, setData] = useState({ entries: [], total_in: 0, total_out: 0, net: 0 });

  const load = () => api.get(`/reports/day-book?from_date=${from}&to_date=${to}`)
    .then(r => setData(r.data)).catch(e => toast.error(formatErr(e)));
  useEffect(() => { load(); }, [from, to]); // eslint-disable-line

  const pg = usePagination(data.entries);
  const tot = data.entries.reduce((a, e) => ({
    labour: a.labour + (e.labour || 0), parts: a.parts + (e.parts || 0),
    amt: a.amt + (e.amt_total || 0),
    drCash: a.drCash + (e.dr_cash || 0), drPaytm: a.drPaytm + (e.dr_paytm || 0), drT: a.drT + (e.dr_total || 0),
    drAcct: a.drAcct + (e.dr_account || 0),
    crCash: a.crCash + (e.cr_cash || 0), crPaytm: a.crPaytm + (e.cr_paytm || 0), crT: a.crT + (e.cr_total || 0),
    crAcct: a.crAcct + (e.cr_account || 0),
    balDr: a.balDr + (e.bal_dr || 0), balCr: a.balCr + (e.bal_cr || 0),
  }), { labour: 0, parts: 0, amt: 0, drCash: 0, drPaytm: 0, drT: 0, drAcct: 0, crCash: 0, crPaytm: 0, crT: 0, crAcct: 0, balDr: 0, balCr: 0 });
  const [org, setOrg] = useState(null);
  useEffect(() => { api.get("/organisation").then(r => setOrg((r.data || [])[0] || null)).catch(() => {}); }, []);
  const exportPdf = async () => {
    try {
      const res = await fetch(`${API}/reports/day-book/pdf?from_date=${from}&to_date=${to}`, {
        headers: { Authorization: `Bearer ${localStorage.getItem("erp_token")}` }, credentials: "include",
      });
      if (!res.ok) throw new Error("Download failed");
      const blob = await res.blob();
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob); a.download = "day_book.pdf"; a.click();
    } catch (e) { toast.error(formatErr(e)); }
  };
  return (
    <div className="p-8 print:hidden">
      <div className="flex items-end justify-between mb-6 gap-4 flex-wrap">
        <div>
          <div className="text-primary text-xs uppercase tracking-[0.3em] mb-2 flex items-center gap-2"><BookOpen className="w-3 h-3"/> Accounts · All financial activity per day</div>
          <h1 className="font-display font-black text-4xl uppercase tracking-tighter">Day Book</h1>
        </div>
        <div className="flex items-end gap-3">
          <div><Label className="text-xs uppercase tracking-[0.15em] text-muted-foreground">From</Label><Input data-testid="from-date" type="date" value={from} onChange={(e) => setFrom(e.target.value)} className="mt-1 rounded-sm bg-secondary border-border w-40"/></div>
          <div><Label className="text-xs uppercase tracking-[0.15em] text-muted-foreground">To</Label><Input data-testid="to-date" type="date" value={to} onChange={(e) => setTo(e.target.value)} className="mt-1 rounded-sm bg-secondary border-border w-40"/></div>
          <Button data-testid="export-pdf-daybook" size="sm" variant="outline" onClick={exportPdf} className="rounded-sm text-xs uppercase tracking-wider"><FileDown className="w-3 h-3 mr-1"/> PDF</Button>
          <Button data-testid="print-daybook" size="sm" variant="outline" onClick={() => window.print()} className="rounded-sm text-xs uppercase tracking-wider"><Printer className="w-3 h-3 mr-1"/> Print</Button>
        </div>
      </div>

      <div className="grid grid-cols-3 gap-3 mb-6">
        <div className="border border-border bg-card rounded-sm p-4">
          <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground flex items-center gap-1"><ArrowDownCircle className="w-3 h-3 text-green-500"/> Money In</div>
          <div className="font-display font-black text-2xl text-green-500 mt-1" data-testid="total-in">{inr(data.total_in)}</div>
        </div>
        <div className="border border-border bg-card rounded-sm p-4">
          <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground flex items-center gap-1"><ArrowUpCircle className="w-3 h-3 text-destructive"/> Money Out</div>
          <div className="font-display font-black text-2xl text-destructive mt-1" data-testid="total-out">{inr(data.total_out)}</div>
        </div>
        <div className="border border-primary/40 bg-primary/5 rounded-sm p-4">
          <div className="text-[10px] uppercase tracking-[0.2em] text-primary">Net</div>
          <div className={`font-display font-black text-2xl mt-1 ${data.net >= 0 ? "text-primary" : "text-destructive"}`} data-testid="net-total">{inr(data.net)}</div>
        </div>
      </div>

      <div className="border border-border bg-card rounded-sm overflow-hidden overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-secondary text-[10px] uppercase tracking-[0.2em] text-muted-foreground">
            <tr>
              <th rowSpan={2} className="text-left p-3 align-bottom">Date</th>
              <th rowSpan={2} className="text-left p-3 align-bottom">Ref #</th>
              <th rowSpan={2} className="text-left p-3 align-bottom">Type</th>
              <th rowSpan={2} className="text-left p-3 align-bottom">Party / Category</th>
              <th colSpan={3} className="text-center p-2 border-l border-b border-border">Amount</th>
              <th colSpan={4} className="text-center p-2 border-l border-b border-border">Debit</th>
              <th colSpan={4} className="text-center p-2 border-l border-b border-border">Credit</th>
              <th colSpan={2} className="text-center p-2 border-l border-b border-border">Balance</th>
            </tr>
            <tr>
              <th className="text-right p-2 border-l border-border">Labour</th>
              <th className="text-right p-2">Parts</th>
              <th className="text-right p-2">Total</th>
              <th className="text-right p-2 border-l border-border">Cash</th>
              <th className="text-right p-2">Paytm</th>
              <th className="text-right p-2">Total</th>
              <th className="text-right p-2">Account</th>
              <th className="text-right p-2 border-l border-border">Cash</th>
              <th className="text-right p-2">Paytm</th>
              <th className="text-right p-2">Total</th>
              <th className="text-right p-2">Account</th>
              <th className="text-right p-2 border-l border-border">Debit</th>
              <th className="text-right p-2">Credit</th>
            </tr>
          </thead>
          <tbody>
            {data.entries.length === 0 && <tr><td colSpan={17} className="p-6 text-center text-muted-foreground">No entries in this range</td></tr>}
            {pg.pageItems.map((e, i) => (
              <tr key={i} data-testid={`day-row-${i}`} className="border-t border-border">
                <td className="p-3 font-mono text-xs">{e.date}</td>
                <td className="p-3 font-mono text-xs text-primary uppercase">{e.reference}</td>
                <td className="p-3 text-xs">{e.type}</td>
                <td className="p-3 text-xs">{e.party}</td>
                <td className="p-3 text-right font-mono text-xs border-l border-border" data-testid={`day-labour-${i}`}>{e.labour ? inr(e.labour) : "—"}</td>
                <td className="p-3 text-right font-mono text-xs" data-testid={`day-parts-${i}`}>{e.parts ? inr(e.parts) : "—"}</td>
                <td className="p-3 text-right font-mono text-xs font-semibold" data-testid={`day-amt-total-${i}`}>{e.amt_total ? inr(e.amt_total) : "—"}</td>
                <td className="p-3 text-right font-mono text-xs border-l border-border" data-testid={`day-dr-cash-${i}`}>{e.dr_cash ? inr(e.dr_cash) : "—"}</td>
                <td className="p-3 text-right font-mono text-xs" data-testid={`day-dr-paytm-${i}`}>{e.dr_paytm ? inr(e.dr_paytm) : "—"}</td>
                <td className="p-3 text-right font-mono text-xs font-semibold" data-testid={`day-dr-total-${i}`}>{e.dr_total ? inr(e.dr_total) : "—"}</td>
                <td className="p-3 text-right font-mono text-xs" data-testid={`day-dr-account-${i}`}>{e.dr_account ? inr(e.dr_account) : "—"}</td>
                <td className="p-3 text-right font-mono text-xs border-l border-border" data-testid={`day-cr-cash-${i}`}>{e.cr_cash ? inr(e.cr_cash) : "—"}</td>
                <td className="p-3 text-right font-mono text-xs" data-testid={`day-cr-paytm-${i}`}>{e.cr_paytm ? inr(e.cr_paytm) : "—"}</td>
                <td className="p-3 text-right font-mono text-xs font-semibold" data-testid={`day-cr-total-${i}`}>{e.cr_total ? inr(e.cr_total) : "—"}</td>
                <td className="p-3 text-right font-mono text-xs" data-testid={`day-cr-account-${i}`}>{e.cr_account ? inr(e.cr_account) : "—"}</td>
                <td className="p-3 text-right font-mono text-xs border-l border-border text-yellow-500" data-testid={`day-bal-dr-${i}`}>{e.bal_dr ? inr(e.bal_dr) : "—"}</td>
                <td className="p-3 text-right font-mono text-xs text-yellow-500" data-testid={`day-bal-cr-${i}`}>{e.bal_cr ? inr(e.bal_cr) : "—"}</td>
              </tr>
            ))}
            {data.entries.length > 0 && (
              <tr className="border-t-2 border-border bg-secondary/50 font-semibold text-xs" data-testid="day-totals-row">
                <td className="p-3" colSpan={4}>TOTALS · {data.entries.length} entries</td>
                <td className="p-3 text-right font-mono border-l border-border">{inr(tot.labour)}</td>
                <td className="p-3 text-right font-mono">{inr(tot.parts)}</td>
                <td className="p-3 text-right font-mono">{inr(tot.amt)}</td>
                <td className="p-3 text-right font-mono border-l border-border">{inr(tot.drCash)}</td>
                <td className="p-3 text-right font-mono">{inr(tot.drPaytm)}</td>
                <td className="p-3 text-right font-mono">{inr(tot.drT)}</td>
                <td className="p-3 text-right font-mono">{inr(tot.drAcct)}</td>
                <td className="p-3 text-right font-mono border-l border-border">{inr(tot.crCash)}</td>
                <td className="p-3 text-right font-mono">{inr(tot.crPaytm)}</td>
                <td className="p-3 text-right font-mono">{inr(tot.crT)}</td>
                <td className="p-3 text-right font-mono">{inr(tot.crAcct)}</td>
                <td className="p-3 text-right font-mono border-l border-border text-yellow-500" data-testid="day-bal-dr-total">{inr(tot.balDr)}</td>
                <td className="p-3 text-right font-mono text-yellow-500" data-testid="day-bal-cr-total">{inr(tot.balCr)}</td>
              </tr>
            )}
          </tbody>
        </table>
        <PaginationBar pg={pg}/>
      </div>

      <div className="hidden print:block print:bg-white print:text-black" id="print-daybook">
        <div className="p-4">
          <div className="text-xl font-bold uppercase">{org?.business_name || "SynqMoto ERP Workshop"}</div>
          <div className="text-[11px] uppercase">Establishment: {org?.establishment_name || org?.branch_name || org?.business_name || "—"}</div>
          <div className="text-lg font-bold mt-2">Day Book</div>
          <div className="text-[11px] mb-2">Period: {from} to {to} · {data.entries.length} entries</div>
          <table className="w-full text-[9px] border-collapse">
            <thead>
              <tr>
                <th rowSpan={2} className="border border-black p-1 text-left">Date</th>
                <th rowSpan={2} className="border border-black p-1 text-left">Ref #</th>
                <th rowSpan={2} className="border border-black p-1 text-left">Type</th>
                <th rowSpan={2} className="border border-black p-1 text-left">Party / Category</th>
                <th colSpan={3} className="border border-black p-1 text-center">Amount</th>
                <th colSpan={4} className="border border-black p-1 text-center">Debit</th>
                <th colSpan={4} className="border border-black p-1 text-center">Credit</th>
                <th colSpan={2} className="border border-black p-1 text-center">Balance</th>
              </tr>
              <tr>
                <th className="border border-black p-1 text-right">Labour</th>
                <th className="border border-black p-1 text-right">Parts</th>
                <th className="border border-black p-1 text-right">Total</th>
                <th className="border border-black p-1 text-right">Cash</th>
                <th className="border border-black p-1 text-right">Paytm</th>
                <th className="border border-black p-1 text-right">Total</th>
                <th className="border border-black p-1 text-right">Account</th>
                <th className="border border-black p-1 text-right">Cash</th>
                <th className="border border-black p-1 text-right">Paytm</th>
                <th className="border border-black p-1 text-right">Total</th>
                <th className="border border-black p-1 text-right">Account</th>
                <th className="border border-black p-1 text-right">Debit</th>
                <th className="border border-black p-1 text-right">Credit</th>
              </tr>
            </thead>
            <tbody>
              {data.entries.map((e, i) => (
                <tr key={i}>
                  <td className="border border-black p-1">{e.date}</td>
                  <td className="border border-black p-1">{e.reference}</td>
                  <td className="border border-black p-1">{e.type}</td>
                  <td className="border border-black p-1">{e.party}</td>
                  <td className="border border-black p-1 text-right">{e.labour ? inr(e.labour) : "—"}</td>
                  <td className="border border-black p-1 text-right">{e.parts ? inr(e.parts) : "—"}</td>
                  <td className="border border-black p-1 text-right">{e.amt_total ? inr(e.amt_total) : "—"}</td>
                  <td className="border border-black p-1 text-right">{e.dr_cash ? inr(e.dr_cash) : "—"}</td>
                  <td className="border border-black p-1 text-right">{e.dr_paytm ? inr(e.dr_paytm) : "—"}</td>
                  <td className="border border-black p-1 text-right">{e.dr_total ? inr(e.dr_total) : "—"}</td>
                  <td className="border border-black p-1 text-right">{e.dr_account ? inr(e.dr_account) : "—"}</td>
                  <td className="border border-black p-1 text-right">{e.cr_cash ? inr(e.cr_cash) : "—"}</td>
                  <td className="border border-black p-1 text-right">{e.cr_paytm ? inr(e.cr_paytm) : "—"}</td>
                  <td className="border border-black p-1 text-right">{e.cr_total ? inr(e.cr_total) : "—"}</td>
                  <td className="border border-black p-1 text-right">{e.cr_account ? inr(e.cr_account) : "—"}</td>
                  <td className="border border-black p-1 text-right">{e.bal_dr ? inr(e.bal_dr) : "—"}</td>
                  <td className="border border-black p-1 text-right">{e.bal_cr ? inr(e.bal_cr) : "—"}</td>
                </tr>
              ))}
              <tr className="font-bold">
                <td className="border border-black p-1" colSpan={4}>TOTALS · {data.entries.length} entries</td>
                <td className="border border-black p-1 text-right">{inr(tot.labour)}</td>
                <td className="border border-black p-1 text-right">{inr(tot.parts)}</td>
                <td className="border border-black p-1 text-right">{inr(tot.amt)}</td>
                <td className="border border-black p-1 text-right">{inr(tot.drCash)}</td>
                <td className="border border-black p-1 text-right">{inr(tot.drPaytm)}</td>
                <td className="border border-black p-1 text-right">{inr(tot.drT)}</td>
                <td className="border border-black p-1 text-right">{inr(tot.drAcct)}</td>
                <td className="border border-black p-1 text-right">{inr(tot.crCash)}</td>
                <td className="border border-black p-1 text-right">{inr(tot.crPaytm)}</td>
                <td className="border border-black p-1 text-right">{inr(tot.crT)}</td>
                <td className="border border-black p-1 text-right">{inr(tot.crAcct)}</td>
                <td className="border border-black p-1 text-right">{inr(tot.balDr)}</td>
                <td className="border border-black p-1 text-right">{inr(tot.balCr)}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
